import React, { useEffect } from "react";
import { ScanLine, X, LogOut } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { fetchFileHistory } from "./ScanningSlice";
import { logout } from "../auth/AuthSlice";

export interface RecentUpload {
    id: string;
    subject: string;
    code: string;
    fileName: string;
    date: string;
    status: 'Evaluated' | 'Processing' | 'Failed';
    s3Url: string; // Added s3Url
}

interface ScanningSidebarProps {
    onClose?: () => void;
}

const ScanningSidebar: React.FC<ScanningSidebarProps> = ({ onClose }) => {
    const dispatch = useAppDispatch();
    const { fileHistory } = useAppSelector((state) => state.scanning);

    useEffect(() => {
        dispatch(fetchFileHistory({ limit: 10 }));
    }, [dispatch]);

    const handleLogout = () => {
        dispatch(logout());
    };

    const formatTimeAgo = (dateString: string) => {
        if (!dateString) return 'Just now';
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) return 'Just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes} min ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours} hours ago`;
        const days = Math.floor(hours / 24);
        return `${days} days ago`;
    };

    const recentUploads: RecentUpload[] = fileHistory.map((file) => ({
        id: file.file_id,
        subject: file.subject_name || 'Unknown',
        code: file.paper_code || 'N/A',
        fileName: file.file_name || 'Untitled',
        date: formatTimeAgo(file.created_at),
        status: file.status === 'processed' ? 'Evaluated' : 'Processing',
        s3Url: file.s3_url, // Map s3_url from response
    }));

    const getStatusIndicator = (status: string) => {
        switch (status) {
            case "Evaluated": return "bg-green-500";
            case "Processing": return "bg-blue-500";
            case "Failed": return "bg-red-500";
            default: return "bg-gray-500";
        }
    };

    return (
        <div className="flex h-full w-full bg-transparent flex-col text-gray-300">
            {/* Sidebar Header */}
            <div className="p-4">
                <div className="flex items-center justify-between mb-6 px-2 pt-2">
                    <div className="flex items-center gap-2 text-white font-bold text-lg">
                        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                            <ScanLine className="w-5 h-5 text-white" />
                        </div>
                        <span>Scanning</span>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Exit button removed */}
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="md:hidden p-1 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* List of Recent Uploads */}
            <div className="flex-1 overflow-y-auto scrollbar-hide px-3 pb-3">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 px-2">
                    Recent Uploads
                </div>

                <div className="space-y-0.5">
                    {recentUploads.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-500">
                            No recent uploads found
                        </div>
                    ) : (
                        recentUploads.map((upload) => (
                            <div
                                key={upload.id}
                                onClick={() => window.open(upload.s3Url, '_blank')} // Open PDF on click
                                className="group flex items-center justify-between px-3 py-2.5 cursor-pointer rounded-md transition-all hover:bg-white/5 text-gray-400 hover:text-gray-200"
                            >
                                <div className="flex items-center gap-3 overflow-hidden w-full">
                                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${getStatusIndicator(upload.status)}`} />
                                    <div className="flex flex-col truncate w-full">
                                        <div className="flex justify-between items-center w-full">
                                            <span className="text-sm font-medium truncate text-gray-300 group-hover:text-white capitalize" title={upload.subject}>
                                                {upload.subject}
                                            </span>
                                            <span className="text-[10px] text-gray-600 group-hover:text-gray-500 shrink-0 ml-2">
                                                {upload.date}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between gap-2 mt-0.5">
                                            <span className="text-[10px] text-gray-500 truncate group-hover:text-gray-400 min-w-0 flex-1 text-left" title={upload.fileName}>
                                                {upload.fileName}
                                            </span>
                                            {upload.code !== 'N/A' && (
                                                <span className="text-[10px] text-gray-600 shrink-0 group-hover:text-gray-500 bg-white/5 px-1.5 py-0.5 rounded">
                                                    {upload.code}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Footer Logout Button */}
            <div className="p-4 border-t border-white/5 mt-auto">
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-3 text-red-400 hover:text-red-300 hover:bg-white/5 rounded-xl transition-all group"
                >
                    <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                        <LogOut className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col items-start">
                        <span className="text-sm font-bold">Logout</span>
                        <span className="text-[10px] text-gray-500 group-hover:text-gray-400">End Session</span>
                    </div>
                </button>
            </div>
        </div>
    );
};

export default ScanningSidebar;
